import { Alert, Box, CircularProgress, Snackbar, Stack } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  cancelOssOrder,
  createOssChange,
  deleteOssOrder,
  deleteOssDocument,
  fetchOssChanges,
  fetchOssOrderDetail,
  getOssDocumentDownloadUrl,
  processOssAction,
  reviewOssChange,
  uploadOssDocument,
} from "../../api/ossApi";
import { OssChangeRequestDialog } from "../../components/changes/OssChangeRequestDialog";
import { OssDocumentUploadDialog } from "../../components/documents/OssDocumentUploadDialog";
import { OssOrderFormDialog } from "../../components/OssOrderFormDialog";
import { OssActionProcessDialog } from "../../components/actions/OssActionProcessDialog";
import { ConfirmDialog } from "../../components/orderDetail/OssOrderDetailDialogs";
import { OssOrderDetailHeader } from "../../components/orderDetail/OssDetailPrimitives";
import { OssOrderDetailTabs, OssOrderSummary } from "../../components/orderDetail/OssOrderDetailSections";
import { isActionNeeded } from "../../components/orderDetail/detailUtils";
import { mockOrders } from "../../constants/mockData";

const mockDetail = (orderId) => {
  const matched = mockOrders.find((row) => String(row.order_id || row.order_line_id) === String(orderId)) || mockOrders[0];
  return {
    order: {
      order_id: matched.order_id || matched.order_line_id,
      order_no: matched.order_no,
      customer_po_no: matched.customer_po_no,
      customer_name: matched.customer_name,
      site_name: matched.site_name,
      order_date: "2026-07-15",
      order_status: "RECEIVED",
      source_type: "MANUAL",
      note: "고객 승인 일정 확인이 필요한 장기납기 주문입니다.",
    },
    lines: [matched],
    documents: [],
    histories: [
      { history_type: "STATUS", title: "ORDER", description: "신규 -> RECEIVED", note: "주문 등록", occurred_at: "2026-07-15 09:00:00" },
    ],
    actions: [],
  };
};

export const OssOrderDetail = () => {
  const { group_code, project_code, orderId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changesLoading, setChangesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [actionLine, setActionLine] = useState(null);
  const [changes, setChanges] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setChangesLoading(true);
    setError("");
    try {
      const [detail, changeData] = await Promise.all([
        fetchOssOrderDetail(group_code, project_code, orderId),
        fetchOssChanges(group_code, project_code, { orderId, pageSize: 100 }),
      ]);
      setData(detail);
      setChanges(changeData?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || "주문 상세를 불러오지 못했습니다.");
      setData(mockDetail(orderId));
      setChanges([]);
    } finally {
      setLoading(false);
      setChangesLoading(false);
    }
  }, [group_code, project_code, orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const order = data?.order || {};
  const lines = useMemo(() => data?.lines || [], [data?.lines]);
  const openLines = useMemo(
    () => lines.filter((line) => line.line_status !== "CANCELLED"),
    [lines]
  );

  const summary = useMemo(() => {
    const target = openLines.length ? openLines : lines;
    const minMargin = target
      .map((line) => Number(line.available_margin_days))
      .filter(Number.isFinite)
      .sort((a, b) => a - b)[0];

    return {
      lineCount: target.length,
      actionCount: target.filter(isActionNeeded).length,
      earliestStart: target.map((line) => line.required_start_date).filter(Boolean).sort()[0],
      earliestCheck: target.map((line) => line.check_required_date).filter(Boolean).sort()[0],
      minMargin: minMargin ?? "-",
    };
  }, [lines, openLines]);

  const submitCancel = async () => {
    setSubmitting(true);
    try {
      await cancelOssOrder(group_code, project_code, orderId, {
        reason: "사용자 화면에서 주문 취소",
        userKey: "order.manager",
      });
      setCancelOpen(false);
      setToast("주문을 취소했습니다.");
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "주문 취소 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitDelete = async () => {
    setSubmitting(true);
    try {
      await deleteOssOrder(group_code, project_code, orderId);
      setDeleteOpen(false);
      setToast("주문을 삭제했습니다.");
      navigate("../orders", { replace: true });
    } catch (e) {
      setError(e.response?.data?.message || "주문 삭제 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitAction = async (payload) => {
    if (!actionLine) return false;
    setSubmitting(true);
    try {
      await processOssAction(group_code, project_code, actionLine.order_line_id, {
        ...payload,
        userKey: "order.manager",
      });
      setToast(payload.actionStatus === "NOTE" ? "조치 이력을 저장했습니다." : payload.nextCheckDate ? "다음 확인일을 예약했습니다." : "조치 처리를 완료했습니다.");
      await load().catch(() => {});
      return true;
    } catch (e) {
      setError(e.response?.data?.message || "조치 처리 중 오류가 발생했습니다.");
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  const dialogOrders = useMemo(
    () =>
      lines.map((line) => ({
        ...line,
        order_id: order.order_id,
        order_no: order.order_no,
        customer_name: order.customer_name,
      })),
    [lines, order.customer_name, order.order_id, order.order_no]
  );

  const actionTicketTarget = useMemo(() => {
    if (!actionLine) return null;
    return {
      ...actionLine,
      order_id: order.order_id || orderId,
      order_no: order.order_no,
      customer_name: order.customer_name,
      site_name: order.site_name,
      exception_title: actionLine.risk_level === "OVERDUE" || actionLine.risk_level === "HIGH" ? "납기 또는 착수 기준 초과" : "일정 확인 필요",
      exception_message: "주문 상세에서 선택한 품목의 일정과 조치 내용을 확인합니다.",
    };
  }, [actionLine, order.customer_name, order.order_id, order.order_no, order.site_name, orderId]);

  const submitDocument = async (file, payload) => {
    setSubmitting(true);
    try {
      await uploadOssDocument(group_code, project_code, file, payload);
      setDocumentOpen(false);
      setToast("문서를 업로드했습니다.");
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "문서 업로드 중 오류가 발생했습니다.");
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  const submitDocumentDelete = async () => {
    if (!documentToDelete) return;
    setSubmitting(true);
    try {
      await deleteOssDocument(group_code, project_code, documentToDelete.document_id);
      setDocumentToDelete(null);
      setToast("문서를 삭제했습니다.");
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "문서 삭제 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const openDocument = (document) => {
    window.open(getOssDocumentDownloadUrl(group_code, project_code, document.document_id), "_blank", "noopener,noreferrer");
  };

  const submitChange = async (payload) => {
    setSubmitting(true);
    try {
      await createOssChange(group_code, project_code, payload);
      setChangeOpen(false);
      setToast("변경 요청을 등록했습니다.");
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "변경 요청 중 오류가 발생했습니다.");
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  const submitReview = async (row, status) => {
    setSubmitting(true);
    try {
      await reviewOssChange(group_code, project_code, row.change_request_id, {
        requestStatus: status,
        reviewedByUserKey: "order.manager",
        reviewNote: status === "APPROVED" ? "주문 상세에서 승인" : "주문 상세에서 반려",
      });
      setToast(status === "APPROVED" ? "변경 승인 및 일정 재계산을 완료했습니다." : "변경 요청을 반려했습니다.");
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "변경 검토 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}>
        <CircularProgress size={34} />
      </Box>
    );
  }

  const disabledByCancel = order.order_status === "CANCELLED";

  return (
    <Stack spacing={2}>
      <OssOrderDetailHeader
        order={order}
        disabledByCancel={disabledByCancel}
        onBack={() => navigate(-1)}
        onEdit={() => setEditOpen(true)}
        onCancel={() => setCancelOpen(true)}
        onDelete={() => setDeleteOpen(true)}
      />

      {error && <Alert severity="warning">{error}</Alert>}

      <OssOrderSummary order={order} summary={summary} />
      <OssOrderDetailTabs
        tab={tab}
        onTabChange={setTab}
        lines={lines}
        data={data}
        changes={changes}
        changesLoading={changesLoading || submitting}
        disabledByCancel={disabledByCancel}
        onAction={setActionLine}
        onDocumentUpload={() => setDocumentOpen(true)}
        onDocumentOpen={openDocument}
        onDocumentDelete={setDocumentToDelete}
        onChangeRequest={() => setChangeOpen(true)}
        onReviewChange={submitReview}
      />

      <OssOrderFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        groupCode={group_code}
        projectCode={project_code}
        orderId={orderId}
        initialData={data}
        onSaved={() => {
          setToast("주문 정보를 수정했습니다.");
          load();
        }}
      />

      <ConfirmDialog
        open={cancelOpen}
        title="주문 취소"
        message="주문과 품목 일정을 취소 상태로 전환합니다. 취소된 주문은 일정 캘린더에서 제외됩니다."
        confirmLabel="취소 처리"
        loading={submitting}
        onClose={() => setCancelOpen(false)}
        onConfirm={submitCancel}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="주문 삭제"
        message="주문과 연결된 품목, 일정, 이력 데이터가 함께 삭제됩니다. 오등록 데이터 정리용으로만 사용해주세요."
        confirmLabel="삭제"
        severity="error"
        loading={submitting}
        onClose={() => setDeleteOpen(false)}
        onConfirm={submitDelete}
      />

      <OssActionProcessDialog
        open={Boolean(actionLine)}
        item={actionTicketTarget}
        groupCode={group_code}
        projectCode={project_code}
        loading={submitting}
        onClose={() => setActionLine(null)}
        onSubmit={submitAction}
      />

      <OssDocumentUploadDialog
        open={documentOpen}
        orders={dialogOrders}
        loading={submitting}
        onClose={() => setDocumentOpen(false)}
        onSubmit={submitDocument}
      />

      <OssChangeRequestDialog
        open={changeOpen}
        orders={dialogOrders}
        loading={submitting}
        onClose={() => setChangeOpen(false)}
        onSubmit={submitChange}
      />

      <ConfirmDialog
        open={Boolean(documentToDelete)}
        title="문서 삭제"
        message="선택한 주문 문서를 삭제합니다."
        confirmLabel="삭제"
        severity="error"
        loading={submitting}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={submitDocumentDelete}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2500}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Stack>
  );
};
